// CartReviewView -- read-only cart review page for sales-assisted carts (TD-125).
//
// Mounted at /m/cart-review/{cart_id}. The customer receives a link via email,
// lands here, sees the cart contents and CTA to continue to checkout.
//
// No auth required to view -- the cart_id in the URL IS the secret.
// Email-confirmation step (before checkout handoff) is a V1 hardening
// measure against URL-leak abuse, NOT a security boundary.
//
// Inline styles only -- no Tailwind. NO imports from portal/.
// Themed via useEmbedTokens(); composes embed-shared primitives.

import { useState, useEffect, useCallback } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedSpinner,
  EmbedButton,
  EmbedBanner,
  EmbedInput,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";

// ---------------------------------------------------------------------------
// Types -- mirrors Go CartReviewResult field-for-field (cart_review_service.go)
// ---------------------------------------------------------------------------

/** Single enriched line in the cart review response. */
export interface CartReviewLine {
  line_id: string;
  product_slug: string;
  product_name: string;
  qty: number;
  unit_price: string;   // decimal string, e.g. "19.99"
  line_total: string;   // decimal string, e.g. "39.98"
}

/**
 * Full response shape for GET /api/v1/marketplace/carts/{id}/review.
 *
 * Matches Go CartReviewResult in cart_review_service.go:116-131.
 * Prices are decimal strings (shopspring/decimal .StringFixed(2)),
 * NOT integer cents like the regular cart/order types.
 */
export interface CartReviewResult {
  cart_id: string;
  status: string;
  created_at_iso: string;
  expires_at_iso: string;
  rep_name: string | null;
  customer_email_masked: string | null;
  lines: CartReviewLine[];
  subtotal: string;       // decimal string, e.g. "39.98"
  currency: string;
  store_name: string;
  store_logo_url: string | null;
  lead_source: string | null;
  cart_notes: string | null;   // rep memo shown above line list (TD-118b)
  version: number;
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type ViewState =
  | { status: "loading" }
  | { status: "ready"; data: CartReviewResult }
  | { status: "not_found" }
  | { status: "conflict" }
  | { status: "gone" }
  | { status: "error"; message: string }
  | { status: "email_prompt"; data: CartReviewResult }
  | { status: "confirmed"; data: CartReviewResult };

// ---------------------------------------------------------------------------
// maskMatchesEmail -- V1 email-confirmation check
// ---------------------------------------------------------------------------

/**
 * Checks whether a user-entered email plausibly matches a masked email
 * from the API (e.g., "j****@example.com").
 *
 * This is NOT a security boundary. The masked email leaks the first
 * character and full domain, so the check is mostly preventing typos
 * and casual abuse (e.g., someone with a leaked URL but no idea who
 * the intended recipient is). Document this trade-off explicitly.
 *
 * Matching rules:
 * - First character of local part must match (case-insensitive)
 * - Domain (everything after @, inclusive) must match (case-insensitive)
 * - If masked is null, return true (defensive -- let through)
 */
export function maskMatchesEmail(masked: string | null, entered: string): boolean {
  if (!masked) return true;

  const maskedAt = masked.indexOf("@");
  if (maskedAt < 0) return false;
  const maskedFirst = masked[0]!.toLowerCase();
  const maskedDomain = masked.slice(maskedAt).toLowerCase();

  const enteredAt = entered.indexOf("@");
  if (enteredAt < 0) return false;
  if (entered.length === 0) return false;
  const enteredFirst = entered[0]!.toLowerCase();
  const enteredDomain = entered.slice(enteredAt).toLowerCase();

  return maskedFirst === enteredFirst && maskedDomain === enteredDomain;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a decimal-string price for display, e.g. "39.98" -> "$39.98". */
function formatDecimalPrice(amount: string, currency: string): string {
  const num = parseFloat(amount);
  if (isNaN(num)) return `$${amount}`;

  const locale = currency === "CAD" ? "en-CA" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/** Compute a human-readable relative time string for an expiration date. */
function expiresInLabel(expiresAtIso: string): string {
  const expires = new Date(expiresAtIso);
  const now = new Date();
  const diffMs = expires.getTime() - now.getTime();

  if (diffMs <= 0) return "expired";

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return "in 1 day";
  if (diffDays <= 30) return `in ${diffDays} days`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return "in 1 week";
  return `in ${diffWeeks} weeks`;
}

/**
 * Fetch cart review data without using the global embedApiFetch (which
 * injects auth tokens from AuthProvider). The cart-review page is
 * unauthenticated -- the cart_id in the URL is the bearer secret.
 */
async function fetchCartReview(apiBase: string, cartId: string, signal?: AbortSignal): Promise<CartReviewResult> {
  const url = `${apiBase}/api/v1/marketplace/carts/${encodeURIComponent(cartId)}/review`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      signal,
    });
  } catch (err) {
    throw new ApiError(0, "NETWORK_ERROR", err instanceof Error ? err.message : "Network request failed");
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const envelope = (payload as { error?: unknown } | null)?.error;
    const errObj = envelope && typeof envelope === "object" ? (envelope as Record<string, unknown>) : {};
    const code = typeof errObj.code === "string" ? errObj.code : "UNKNOWN";
    const message = typeof errObj.message === "string" ? errObj.message : `Request failed (${res.status})`;
    throw new ApiError(res.status, code, message, errObj.details);
  }

  // API wraps in { data: ... } envelope
  const envelope = (payload as { data?: unknown } | null) ?? null;
  return (envelope?.data ?? payload) as CartReviewResult;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CartReviewViewProps {
  cartId: string;
  apiBase: string;
  onContinueToCheckout: (cartId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CartReviewView({ cartId, apiBase, onContinueToCheckout }: CartReviewViewProps) {
  const t = useEmbedTokens();
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  // Fetch cart review data on mount.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const data = await fetchCartReview(apiBase, cartId, controller.signal);
        if (!cancelled) setState({ status: "ready", data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setState({ status: "not_found" });
          } else if (err.status === 409) {
            setState({ status: "conflict" });
          } else if (err.status === 410) {
            setState({ status: "gone" });
          } else {
            setState({ status: "error", message: err.message });
          }
        } else {
          setState({ status: "error", message: "An unexpected error occurred." });
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiBase, cartId]);

  // CTA click handler -- triggers email prompt or skips if no masked email.
  const handleContinueClick = useCallback(() => {
    if (state.status !== "ready") return;
    if (!state.data.customer_email_masked) {
      // No masked email to compare -- skip confirmation (defensive).
      onContinueToCheckout(cartId);
      return;
    }
    setState({ status: "email_prompt", data: state.data });
  }, [state, cartId, onContinueToCheckout]);

  // Email confirmation submit.
  const handleEmailSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (state.status !== "email_prompt") return;
      const trimmed = emailInput.trim();
      if (!trimmed) {
        setEmailError("Please enter your email address.");
        return;
      }
      if (!maskMatchesEmail(state.data.customer_email_masked, trimmed)) {
        setEmailError("The email address does not match our records. Please check and try again.");
        return;
      }
      setEmailError("");
      setState({ status: "confirmed", data: state.data });
      onContinueToCheckout(cartId);
    },
    [state, emailInput, cartId, onContinueToCheckout],
  );

  // -- Render branches --------------------------------------------------------

  // Loading. The outer wrapper owns role="status" for the screen-reader
  // announcement; EmbedSpinner is rendered aria-hidden so there is exactly
  // one status node (the test asserts a single getByRole("status")).
  if (state.status === "loading") {
    return (
      <div style={{ textAlign: "center", padding: t.spacing.xl }} role="status">
        <div style={{ display: "flex", justifyContent: "center" }} aria-hidden>
          <EmbedSpinner size={32} />
        </div>
        <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, marginTop: t.spacing.md }}>
          Loading your cart...
        </p>
      </div>
    );
  }

  // Not found
  if (state.status === "not_found") {
    return (
      <EmbedBanner tone="error">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: t.fontSize.lg, fontWeight: 600, color: t.colors.error, margin: `0 0 ${t.spacing.sm}px 0` }}>
            Cart not found
          </p>
          <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, margin: 0 }}>
            The link may be invalid. Please check the link from your email or contact your sales rep.
          </p>
        </div>
      </EmbedBanner>
    );
  }

  // Gone (expired)
  if (state.status === "gone") {
    return (
      <EmbedBanner tone="error">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: t.fontSize.lg, fontWeight: 600, color: t.colors.error, margin: `0 0 ${t.spacing.sm}px 0` }}>
            Cart invite expired
          </p>
          <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, margin: 0 }}>
            This cart invite has expired. Please contact your sales rep for a new one.
          </p>
        </div>
      </EmbedBanner>
    );
  }

  // Conflict (already used)
  if (state.status === "conflict") {
    return (
      <EmbedBanner tone="error">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: t.fontSize.lg, fontWeight: 600, color: t.colors.error, margin: `0 0 ${t.spacing.sm}px 0` }}>
            Cart already used
          </p>
          <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, margin: 0 }}>
            This cart has already been used. Contact your sales rep if you need help.
          </p>
        </div>
      </EmbedBanner>
    );
  }

  // Generic error
  if (state.status === "error") {
    return (
      <EmbedBanner tone="error">
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: t.fontSize.lg, fontWeight: 600, color: t.colors.error, margin: `0 0 ${t.spacing.sm}px 0` }}>
            Something went wrong
          </p>
          <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.sm, margin: 0 }}>
            We couldn't load your cart right now. Please try refreshing the page.
          </p>
        </div>
      </EmbedBanner>
    );
  }

  // Ready / email_prompt / confirmed -- all render the cart view
  const data = state.data;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: t.spacing.lg }}>
        <h2
          style={{
            fontSize: t.fontSize.xl,
            fontWeight: 700,
            color: t.colors.text,
            margin: 0,
          }}
        >
          {data.store_name}
        </h2>
        {data.rep_name && (
          <p
            style={{
              fontSize: t.fontSize.sm,
              color: t.colors.textSecondary,
              margin: `${t.spacing.xs}px 0 0 0`,
            }}
          >
            Sales rep: {data.rep_name}
          </p>
        )}
      </div>

      {/* Cart notes banner */}
      {data.cart_notes && (
        <div
          style={{
            padding: t.spacing.md,
            backgroundColor: t.colors.primaryTint,
            borderRadius: t.borderRadius,
            marginBottom: t.spacing.lg,
            fontSize: t.fontSize.sm,
            color: t.colors.text,
            lineHeight: t.lineHeight.normal,
          }}
        >
          <strong>Note from your rep:</strong> {data.cart_notes}
        </div>
      )}

      {/* Line items */}
      <div style={{ marginBottom: t.spacing.md }}>
        {data.lines.map((line) => (
          <div
            key={line.line_id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${t.spacing.md}px 0`,
              borderBottom: `1px solid ${t.colors.cardBorder}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  fontSize: t.fontSize.sm,
                  fontWeight: 600,
                  color: t.colors.text,
                }}
              >
                {line.product_name}
              </span>
              <span
                style={{
                  fontSize: t.fontSize.sm,
                  color: t.colors.textSecondary,
                  marginLeft: t.spacing.sm,
                }}
              >
                x {line.qty}
              </span>
            </div>
            <span
              style={{
                fontSize: t.fontSize.sm,
                fontWeight: 600,
                color: t.colors.text,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                marginLeft: t.spacing.md,
              }}
            >
              {formatDecimalPrice(line.line_total, data.currency)}
            </span>
          </div>
        ))}
      </div>

      {/* Subtotal */}
      <div
        style={{
          borderTop: `1px solid ${t.colors.cardBorder}`,
          paddingTop: t.spacing.md,
          marginBottom: t.spacing.md,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text }}>
            Subtotal
          </span>
          <span style={{ fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>
            {formatDecimalPrice(data.subtotal, data.currency)}
          </span>
        </div>
      </div>

      {/* Expires */}
      <div style={{ marginBottom: t.spacing.lg }}>
        <p style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary, margin: 0 }}>
          This cart invite expires {expiresInLabel(data.expires_at_iso)}
        </p>
      </div>

      {/* Email prompt (inline, not modal -- simpler for V1) */}
      {state.status === "email_prompt" && (
        <div style={{ marginBottom: t.spacing.lg }}>
          <EmbedCard>
            <h3
              style={{
                fontSize: t.fontSize.md,
                fontWeight: 600,
                color: t.colors.text,
                margin: `0 0 ${t.spacing.sm}px 0`,
              }}
            >
              Confirm your email
            </h3>
            <p style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, margin: `0 0 ${t.spacing.md}px 0` }}>
              To continue, please enter the email address associated with this cart
              ({data.customer_email_masked}).
            </p>
            <form onSubmit={handleEmailSubmit} noValidate>
              {/* TD-319: the inline style this replaced was a hand-copy of
                  EmbedInput's own token style (same width/height/padding/
                  colors, same error-border rule), so this is a true drop-in —
                  `invalid` maps to what was the conditional border. The
                  bottom margin is layout, not control styling, so it stays on
                  a wrapper rather than being pushed into the primitive. */}
              <div style={{ marginBottom: emailError ? t.spacing.xs : t.spacing.md }}>
                <EmbedInput
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  placeholder="you@example.com"
                  aria-label="Email address"
                  aria-invalid={!!emailError}
                  invalid={!!emailError}
                />
              </div>
              {emailError && (
                <p
                  role="alert"
                  style={{
                    color: t.colors.error,
                    fontSize: t.fontSize.xs,
                    margin: `0 0 ${t.spacing.sm}px 0`,
                  }}
                >
                  {emailError}
                </p>
              )}
              <EmbedButton type="submit" variant="primary" fullWidth>
                Confirm and continue
              </EmbedButton>
            </form>
          </EmbedCard>
        </div>
      )}

      {/* CTA -- visible in ready state, hidden during email prompt */}
      {state.status === "ready" && (
        <EmbedButton type="button" variant="primary" fullWidth onClick={handleContinueClick}>
          Continue to checkout
        </EmbedButton>
      )}
    </div>
  );
}
